import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Plus, Pencil, Loader2, AlertCircle, RefreshCw, Briefcase, RotateCcw, Ban, UserCog, Trash2,
} from 'lucide-react';
import { PageHeader, Button } from '../ui-v2';
import { Modal, Input, Select, Badge, Table, EmptyState, Tooltip } from '../ui-v2.5';
import {
  listPuestos, crearPuesto, actualizarPuesto, desactivarPuesto, listNegocios,
  eliminarPuestoPermanente, listEmpresasColaboradoras,
  alcanceDePersona, vincularPersonaAPuesto,
} from './api';
import { perteneceAlAmbito, nombreDelPadre, INTERNO, COLABORADOR } from './pertenencia';
import EliminarModal from './components/EliminarModal';
import QuienDaDeAltaModal from './QuienDaDeAltaModal';
import PersonaPickerMiembro from './components/PersonaPickerMiembro';
import IncorporacionesTabs from './IncorporacionesTabs';

/**
 * Puestos — pantalla de administración (§3.4 de Fase 5). Tabla con filtro por negocio; editor en
 * Modal (crear/editar/reactivar); desactivar con Modal danger que muta para mostrar los perfiles
 * activos que lo bloquean (409 `puesto_con_perfiles`), igual que Áreas/Recursos.
 *
 * DS: ui-v2.5 (Modal, Input, Select, Badge, Table, EmptyState) + Button/PageHeader de ui-v2. El
 * desplegable de negocios se alimenta del endpoint module-local `/negocios`. toast global; estados
 * carga/vacío/error y reactivación por 409 idénticos a Fase 4.
 */

const AMBITO_TP = { interno: 'Interno', colaborador: 'Colaborador' };
const cuerpoError = (e) => (e && e.response && e.response.data) || {};

/**
 * LOS CAMPOS DEL PADRE DE UN PUESTO, EN UN SOLO SITIO.
 *
 * Un puesto cuelga de un negocio O de una empresa colaboradora, y el servidor exige el que
 * corresponda a su `tipo_persona` (400 `negocio_requerido` / `colaboradora_requerida`). Hay DOS
 * caminos que escriben un puesto -el modal y el boton de reactivar- y el de reactivar mandaba
 * solo `negocio_id`: con un puesto de colaborador habria dado un 400 que parece un fallo del
 * servidor. Por eso los campos se arman aqui y no en cada llamada.
 */
function camposDePadre(tipo, { negocioId, colaboradoraId }) {
  return tipo === COLABORADOR
    ? { tipo_persona: COLABORADOR, empresa_colaboradora_id: Number(colaboradoraId) }
    : { tipo_persona: INTERNO, negocio_id: Number(negocioId) };
}

/** El padre de una fila ya guardada, en la forma que el servidor espera recibir de vuelta. */
const camposDePadreDe = (p) => camposDePadre(p.tipo_persona || INTERNO, {
  negocioId: p.negocio_id, colaboradoraId: p.empresa_colaboradora_id,
});

export default function Puestos() {
  const [puestos, setPuestos] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [colaboradoras, setColaboradoras] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  const [accionId, setAccionId] = useState(null);
  /* El filtro guarda `neg:<id>` o `col:<id>`, no un numero: los ids de `negocios` y de
   * `empresas_colaboradoras` se solapan -hay un negocio 1 y una colaboradora 1-, asi que un id
   * pelado no dice de que tabla es. Es el mismo motivo por el que `_helpers.js` del backend no
   * fusiona `personasBasicas` y `colaboradorasBasicas` en un solo Map. */
  const [filtroPadre, setFiltroPadre] = useState(''); // '' = todos
  const [editor, setEditor] = useState(null);       // { puesto|null }
  const [aDesactivar, setADesactivar] = useState(null);
  const [aEliminar, setAEliminar] = useState(null);
  const [verQuien, setVerQuien] = useState(null);   // { puesto }

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(false);
    try {
      const [ps, ns, cs] = await Promise.all([listPuestos(), listNegocios(), listEmpresasColaboradoras()]);
      setPuestos(ps);
      setNegocios(ns?.negocios || []);
      setColaboradoras(cs || []);
    } catch (e) {
      console.error('[incorp][puestos] cargar', e);
      setError(true);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtrados = useMemo(() => {
    if (!filtroPadre) return puestos;
    const [clase, id] = filtroPadre.split(':');
    return puestos.filter((p) => perteneceAlAmbito(p, clase === 'col'
      ? { tipo: COLABORADOR, colaboradoraId: id }
      : { tipo: INTERNO, negocioId: id }));
  }, [puestos, filtroPadre]);

  async function reactivar(p) {
    setAccionId(p.id);
    try {
      await actualizarPuesto(p.id, { ...camposDePadreDe(p), nombre: p.nombre, activo: true });
      toast.success(`Puesto «${p.nombre}» reactivado.`);
      await cargar();
    } catch (e) {
      console.error('[incorp][puestos] reactivar', e);
      toast.error(cuerpoError(e).message || 'No se pudo reactivar el puesto.');
    } finally {
      setAccionId(null);
    }
  }

  const columns = [
    {
      key: 'nombre', header: 'Nombre',
      render: (p) => (
        <div className="flex items-center gap-2">
          <span className={`font-medium ${p.activo ? 'text-v25-ink-900' : 'text-v25-ink-400'}`}>{p.nombre}</span>
          {!p.activo && <Badge tone="expired">Desactivado</Badge>}
        </div>
      ),
    },
    {
      key: 'tipo', header: 'Tipo', width: 130,
      render: (p) => <Badge tone={(p.tipo_persona || INTERNO) === COLABORADOR ? 'received' : 'neutral'}>{AMBITO_TP[p.tipo_persona || INTERNO]}</Badge>,
    },
    {
      key: 'personas', header: 'Dan de alta', width: 140,
      /* El recuento EN LA FILA, no detras de una accion. Es el mismo criterio que el «· N pers.»
       * de Areas: saber quien tiene acceso a que no deberia costar un clic por fila.
       *
       * Y el cero se dice con palabras y no con un «0»: «0» se lee como un dato que falta;
       * «nadie acotado» dice lo que significa —que ese puesto no restringe a nadie—. */
      render: (p) => {
        const n = p.personas_count || 0;
        if (!n) return <span className="text-sm text-v25-ink-400">nadie acotado</span>;
        const nombres = (p.personas || []).map((x) => x.nombre).join(', ');
        return (
          <Tooltip content={nombres}>
            <button
              type="button"
              onClick={() => setVerQuien({ puesto: p })}
              className="text-sm text-v25-ink-700 underline decoration-dotted underline-offset-2 hover:text-v25-ink-900"
            >
              {n} {n === 1 ? 'persona' : 'personas'}
            </button>
          </Tooltip>
        );
      },
    },
    {
      key: 'padre', header: 'Depende de',
      /* Un puesto de interno depende de su negocio; uno de colaborador, de su empresa
       * colaboradora. La columna es una, porque para quien mira la tabla es el mismo dato. */
      render: (p) => <span className="text-v25-ink-700">{nombreDelPadre(p) || '—'}</span>,
    },
    {
      key: 'acciones', header: '', width: 190,
      render: (p) => (
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => setEditor({ puesto: p })}><Pencil />Editar</Button>
          {/* Quien puede dar de alta AQUI. Va en la fila del puesto y no en una pantalla aparte
              porque la pregunta es «¿quien da de alta en ESTE?», y una lista global de personas
              con sus puestos obligaria a buscar el puesto dentro de cada persona. */}
          <Tooltip content="Quién puede dar de alta aquí">
            <Button variant="ghost" size="icon" onClick={() => setVerQuien({ puesto: p })}><UserCog /></Button>
          </Tooltip>
          {p.activo ? (
            <Tooltip content="Desactivar"><Button variant="ghost" size="icon" onClick={() => setADesactivar(p)}><Ban /></Button></Tooltip>
          ) : (
            <Tooltip content="Reactivar">
              <Button variant="ghost" size="icon" disabled={accionId === p.id} onClick={() => reactivar(p)}>
                {accionId === p.id ? <Loader2 className="animate-spin" /> : <RotateCcw />}
              </Button>
            </Tooltip>
          )}
          <span className="mx-0.5 h-5 w-px shrink-0 bg-v25-line" aria-hidden="true" />
          <Tooltip content="Eliminar permanentemente">
            <Button variant="ghost" size="icon" onClick={() => setAEliminar(p)} className="text-red-600 hover:bg-red-50 hover:text-red-700">
              <Trash2 />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Puestos"
        subtitle="Puestos de cada negocio y de cada empresa colaboradora; base de los perfiles de incorporación."
        action={<Button onClick={() => setEditor({ puesto: null })}><Plus />Nuevo puesto</Button>}
      />

      <IncorporacionesTabs active="puestos" />

      {/* Filtro por negocio */}
      {!cargando && !error && puestos.length > 0 && (
        <Select
          label="Filtrar por negocio o empresa colaboradora"
          value={filtroPadre}
          onChange={(e) => setFiltroPadre(e.target.value)}
          wrapperClassName="max-w-[340px]"
        >
          <option value="">Todos</option>
          <optgroup label="Negocios">
            {negocios.map((n) => <option key={`neg-${n.id}`} value={`neg:${n.id}`}>{n.nombre}</option>)}
          </optgroup>
          <optgroup label="Empresas colaboradoras">
            {colaboradoras.map((c) => <option key={`col-${c.id}`} value={`col:${c.id}`}>{c.nombre}</option>)}
          </optgroup>
        </Select>
      )}

      {cargando ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-v25-ink-500">
          <Loader2 className="h-5 w-5 animate-spin" /> Cargando puestos…
        </div>
      ) : error ? (
        <div className="rounded-v25-lg border border-v25-line bg-white py-16 text-center">
          <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-500" />
          <p className="mb-4 text-sm text-red-600">No se pudieron cargar los puestos.</p>
          <Button variant="outline" onClick={cargar}><RefreshCw />Reintentar</Button>
        </div>
      ) : puestos.length === 0 ? (
        <EmptyState
          variant="medium"
          icon={Briefcase}
          title="Aún no hay puestos"
          description="Crea el primer puesto asociándolo a un negocio o a una empresa colaboradora."
          action={<Button onClick={() => setEditor({ puesto: null })}><Plus />Nuevo puesto</Button>}
        />
      ) : filtrados.length === 0 ? (
        <EmptyState variant="minimal" title="Ningún puesto en lo seleccionado." />
      ) : (
        <Table columns={columns} rows={filtrados} />
      )}

      {editor && (
        <PuestoModal
          puesto={editor.puesto}
          negocios={negocios}
          colaboradoras={colaboradoras}
          onClose={() => setEditor(null)}
          onGuardado={() => { setEditor(null); cargar(); }}
        />
      )}

      {verQuien && (
        <QuienDaDeAltaModal puesto={verQuien.puesto} onClose={() => { setVerQuien(null); cargar(); }} />
      )}

      {aDesactivar && (
        <DesactivarPuestoModal
          puesto={aDesactivar}
          onClose={() => setADesactivar(null)}
          onDesactivado={() => { setADesactivar(null); cargar(); }}
        />
      )}

      {aEliminar && (
        <EliminarModal
          titulo="Eliminar puesto"
          subtitulo={nombreDelPadre(aEliminar) ? `${aEliminar.nombre} · ${nombreDelPadre(aEliminar)}` : aEliminar.nombre}
          exitoMsg={`Puesto «${aEliminar.nombre}» eliminado permanentemente.`}
          eliminar={() => eliminarPuestoPermanente(aEliminar.id)}
          onClose={() => setAEliminar(null)}
          onEliminado={() => { setAEliminar(null); cargar(); }}
        >
          Se eliminará el puesto <strong>«{aEliminar.nombre}»</strong> de forma permanente. Desaparece del
          catálogo de {nombreDelPadre(aEliminar) || 'su negocio'}. Esta acción no se puede deshacer.
        </EliminarModal>
      )}
    </div>
  );
}

// ─────────────────────────── Modal editor (crear/editar) ───────────────────────────
function PuestoModal({ puesto, negocios, colaboradoras, onClose, onGuardado }) {
  const esEdicion = !!(puesto && puesto.id);
  const [nombre, setNombre] = useState(puesto?.nombre || '');
  const [tipo, setTipo] = useState(puesto?.tipo_persona || INTERNO);
  const [negocioId, setNegocioId] = useState(puesto?.negocio_id ? String(puesto.negocio_id) : '');
  const [colaboradoraId, setColaboradoraId] = useState(puesto?.empresa_colaboradora_id ? String(puesto.empresa_colaboradora_id) : '');
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState({}); // { nombre?, padre?, tipo? }
  const [reactivable, setReactivable] = useState(null); // { id, nombre } del 409
  /* ── PERSONAS A VINCULAR AL CREAR ──────────────────────────────────────────────────────
   *
   * Solo al CREAR: en edicion ya existe la accion «Quien puede dar de alta aqui», y tener dos
   * sitios que hacen lo mismo sobre un puesto que ya existe es pedir que diverjan.
   *
   * Se acumulan EN MEMORIA y se vinculan despues de crear. El aviso del primer vinculo no
   * necesita que el puesto exista: «¿es el primero de esta persona?» es una pregunta sobre LA
   * PERSONA, y la respuesta no cambia al crear el puesto. */
  const [aVincular, setAVincular] = useState([]);   // [{ id, nombre, sera_su_primero }]
  const [confirmarPrimero, setConfirmarPrimero] = useState(null);
  const [fallosVinculo, setFallosVinculo] = useState([]);

  const esColab = tipo === COLABORADOR;

  function validar() {
    const err = {};
    const n = nombre.trim();
    if (!n) err.nombre = 'El nombre es obligatorio.';
    else if (n.length > 100) err.nombre = 'Máximo 100 caracteres.';
    /* Se pide el padre QUE CORRESPONDE AL TIPO, no los dos: pedir los dos y mandar los dos es lo
     * que el servidor rechaza. La columna que no toca viaja a NULL. */
    if (esColab && !colaboradoraId) err.padre = 'Selecciona una empresa colaboradora.';
    if (!esColab && !negocioId) err.padre = 'Selecciona un negocio.';
    setErrores(err);
    if (Object.keys(err).length) return null;
    return { nombre: n, ...camposDePadre(tipo, { negocioId, colaboradoraId }) };
  }

  /* Al elegir, se pregunta al servidor cuantos vinculos tiene YA. Si son cero, esto se lo
   * acotara, y eso se avisa antes de nada —no al final, cuando ya se ha creado el puesto—. */
  async function alElegirPersona(persona) {
    if (aVincular.some((x) => x.id === persona.id)) return;
    setGuardando(true);
    try {
      const a = await alcanceDePersona(persona.id);
      const fila = { id: persona.id, nombre: persona.nombre, sera_su_primero: a.vinculos === 0 };
      if (fila.sera_su_primero) setConfirmarPrimero(fila);
      else setAVincular((xs) => [...xs, fila]);
    } catch (e) {
      console.error('[incorp][puestos] alcanceDePersona', e);
      toast.error((e && e.response && e.response.data && e.response.data.message)
        || 'No se pudo comprobar el alcance de esa persona.');
    } finally {
      setGuardando(false);
    }
  }

  async function guardar() {
    const payload = validar();
    if (!payload) return;
    setGuardando(true);
    setReactivable(null);
    try {
      if (esEdicion) {
        await actualizarPuesto(puesto.id, payload);
        toast.success('Puesto actualizado.');
      } else {
        /* ── CREAR Y VINCULAR POR SEPARADO, DICIENDO QUE PASO ──────────────────────────
         *
         * No hay un endpoint que haga las dos cosas en una transaccion, y es deliberado: lo
         * que cuesta crear es el PUESTO; los vinculos son un clic desde su fila. Un endpoint
         * nuevo obligaria a duplicar la logica de vinculacion en dos sitios —el de uno a uno
         * y el de la creacion— y eso es la copia que llevamos toda la semana deshaciendo.
         *
         * Lo que NO se hace es borrar el puesto si un vinculo falla: convertiria un fallo
         * parcial en perdida de trabajo. Se dice exactamente que quedo sin hacer. */
        const creado = await crearPuesto(payload);
        const fallos = [];
        for (const v of aVincular) {
          try { await vincularPersonaAPuesto(creado.id, v.id); }
          catch (e) {
            fallos.push(`${v.nombre}: ${(e && e.response && e.response.data && e.response.data.message) || 'error'}`);
          }
        }
        if (!fallos.length) {
          toast.success(aVincular.length
            ? `Puesto creado, con ${aVincular.length} persona(s) asignada(s).`
            : 'Puesto creado.');
        } else {
          setFallosVinculo(fallos);
          toast.warning(`Puesto creado, pero ${fallos.length} vínculo(s) no se pudieron hacer. `
            + 'Puedes añadirlos desde «Quién puede dar de alta aquí».');
          setGuardando(false);
          return;   // el modal NO se cierra: lo que fallo tiene que poder leerse
        }
      }
      onGuardado();
    } catch (e) {
      const body = cuerpoError(e);
      if (body.error === 'nombre_duplicado_inactivo' && body.id) {
        setReactivable({ id: body.id, nombre: payload.nombre });
      } else if (body.error === 'nombre_duplicado') {
        setErrores((prev) => ({ ...prev, nombre: esColab
          ? 'Ya existe un puesto con ese nombre en esa empresa colaboradora.'
          : 'Ya existe un puesto con ese nombre en ese negocio.' }));
      } else if (body.error === 'puesto_con_perfiles') {
        /* Cambiar el tipo de un puesto que ya tiene perfiles arrastraria sus recursos a un ambito
         * que no les toca, y el servidor lo para. El numero viene en el 409: decirlo es lo que
         * deja decidir, y callarlo convierte un limite en un fallo. */
        setErrores((prev) => ({ ...prev, tipo: body.message || 'Este puesto tiene perfiles y no puede cambiar de tipo.' }));
      } else {
        console.error('[incorp][puestos] guardar', e);
        toast.error(body.message || 'No se pudo guardar el puesto.');
      }
    } finally {
      setGuardando(false);
    }
  }

  // Reactivar el puesto desactivado homónimo con el id del 409 (aplica los valores del formulario).
  async function reactivarDuplicado() {
    if (!reactivable) return;
    const payload = validar();
    if (!payload) return;
    setGuardando(true);
    try {
      await actualizarPuesto(reactivable.id, { ...payload, activo: true });
      toast.success(`Puesto «${reactivable.nombre}» reactivado.`);
      onGuardado();
    } catch (e) {
      console.error('[incorp][puestos] reactivarDuplicado', e);
      toast.error(cuerpoError(e).message || 'No se pudo reactivar el puesto.');
    } finally {
      setGuardando(false);
    }
  }

  const footer = reactivable ? (
    <>
      <Button variant="outline" onClick={onClose} disabled={guardando}>Cancelar</Button>
      <Button onClick={reactivarDuplicado} disabled={guardando}>
        {guardando ? <Loader2 className="animate-spin" /> : <RotateCcw />}Reactivar el puesto existente
      </Button>
    </>
  ) : (
    <>
      <Button variant="outline" onClick={onClose} disabled={guardando}>Cancelar</Button>
      <Button onClick={guardar} disabled={guardando}>
        {guardando ? <Loader2 className="animate-spin" /> : null}{esEdicion ? 'Guardar cambios' : 'Crear puesto'}
      </Button>
    </>
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={esEdicion ? 'Editar puesto' : 'Nuevo puesto'}
      subtitle={esEdicion ? puesto.nombre : 'Define el nombre, el tipo y de qué depende.'}
      footer={footer}
    >
      <div className="space-y-4">
        <Input
          label="Nombre *"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          error={errores.nombre}
          maxLength={100}
          autoFocus
        />
        <Select
          label="Tipo de persona *"
          value={tipo}
          onChange={(e) => { setTipo(e.target.value); setErrores((p) => ({ ...p, padre: undefined, tipo: undefined })); }}
          error={errores.tipo}
          hint={esColab
            ? 'Un colaborador trabaja para su empresa colaboradora, no para una marca.'
            : 'Un interno trabaja para una de las marcas del grupo.'}
        >
          <option value={INTERNO}>{AMBITO_TP.interno}</option>
          <option value={COLABORADOR}>{AMBITO_TP.colaborador}</option>
        </Select>

        {/* UNO DE LOS DOS, NUNCA LOS DOS. El tipo decide cual se pide; el otro viaja a NULL. */}
        {esColab ? (
          <Select
            label="Empresa colaboradora *"
            value={colaboradoraId}
            onChange={(e) => setColaboradoraId(e.target.value)}
            error={errores.padre}
            hint={colaboradoras.length === 0 ? 'No hay empresas colaboradoras dadas de alta.' : undefined}
          >
            <option value="">Selecciona una empresa…</option>
            {colaboradoras.map((c) => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </Select>
        ) : (
          <Select
            label="Negocio *"
            value={negocioId}
            onChange={(e) => setNegocioId(e.target.value)}
            error={errores.padre}
          >
            <option value="">Selecciona un negocio…</option>
            {negocios.map((n) => (
              <option key={n.id} value={n.id}>{n.nombre}</option>
            ))}
          </Select>
        )}

        {/* ── QUIEN PODRA DAR DE ALTA AQUI, AL CREARLO ──────────────────────────────────
            Solo al crear: en edicion esta la accion de la fila, y dos sitios que hacen lo
            mismo sobre un puesto existente acaban divergiendo. */}
        {!esEdicion && (
          <div className="space-y-2 rounded-v25-md border border-v25-line p-3">
            <span className="block text-[13px] font-medium text-v25-ink-700">
              Quién podrá dar de alta aquí <span className="font-normal text-v25-ink-400">(opcional)</span>
            </span>
            <p className="text-xs leading-relaxed text-v25-ink-500">
              Esto no da permiso: lo <strong>acota</strong>. Quien no tenga ningún puesto asignado
              sigue viéndolo todo.
            </p>
            {aVincular.length > 0 && (
              <ul className="divide-y divide-v25-line rounded-v25-md border border-v25-line">
                {aVincular.map((v) => (
                  <li key={v.id} className="flex items-center justify-between gap-2 p-2 text-sm">
                    <span className="truncate text-v25-ink-900">{v.nombre}</span>
                    <div className="flex shrink-0 items-center gap-2">
                      {v.sera_su_primero && <Badge tone="received">se acota</Badge>}
                      <Button variant="ghost" size="icon" disabled={guardando}
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => setAVincular((xs) => xs.filter((x) => x.id !== v.id))}>
                        <Trash2 />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <PersonaPickerMiembro onAdd={alElegirPersona} excluirIds={aVincular.map((v) => v.id)}
              disabled={guardando} enFlujo placeholder="Añadir persona…" />

            {/* EL AVISO DEL PRIMER VINCULO, antes de crear nada. La pregunta es sobre la
                PERSONA, asi que se puede contestar aunque el puesto no exista todavia. */}
            {confirmarPrimero && (
              <div className="rounded-v25-md border border-v25-received-bd bg-v25-received-bg p-3 text-[13px] leading-relaxed text-v25-received-fg">
                <div className="mb-2 flex items-start gap-2">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    <strong>{confirmarPrimero.nombre}</strong> no tiene ningún puesto asignado, así que
                    hoy ve todas las altas. Al crear este puesto pasará a ver <strong>solo las altas
                    que haya creado</strong> y a dar de alta <strong>solo aquí</strong>.
                  </span>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setConfirmarPrimero(null)}>Cancelar</Button>
                  <Button size="sm" onClick={() => {
                    setAVincular((xs) => [...xs, confirmarPrimero]);
                    setConfirmarPrimero(null);
                  }}>Añadirla igualmente</Button>
                </div>
              </div>
            )}

            {/* LO QUE NO SE PUDO VINCULAR. El puesto SI se creo: decir exactamente que quedo
                sin hacer es lo que convierte un fallo parcial en algo que se puede terminar. */}
            {fallosVinculo.length > 0 && (
              <div className="rounded-v25-md border border-v25-expired-bd bg-v25-expired-bg p-3 text-[13px] leading-relaxed text-v25-expired-fg">
                <strong>El puesto se creó</strong>, pero esto no se pudo vincular:
                <ul className="mt-1 list-disc pl-5">
                  {fallosVinculo.map((f) => <li key={f}>{f}</li>)}
                </ul>
                <span className="mt-1 block">Puedes añadirlos desde «Quién puede dar de alta aquí».</span>
              </div>
            )}
          </div>
        )}

        {reactivable && (
          <div className="flex items-start gap-2 rounded-v25-md border border-v25-received-bd bg-v25-received-bg p-3 text-xs text-v25-received-fg">
            <RotateCcw className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Ya existe un puesto <strong>«{reactivable.nombre}»</strong> desactivado en
              {esColab ? ' esa empresa colaboradora' : ' ese negocio'}. En vez de crear otro, puedes reactivarlo.
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─────────────────────────── Modal danger (desactivar) ───────────────────────────
function DesactivarPuestoModal({ puesto, onClose, onDesactivado }) {
  const [procesando, setProcesando] = useState(false);
  const [bloqueo, setBloqueo] = useState(null); // perfiles que lo usan (409)

  async function confirmar() {
    setProcesando(true);
    try {
      await desactivarPuesto(puesto.id);
      toast.success(`Puesto «${puesto.nombre}» desactivado.`);
      onDesactivado();
    } catch (e) {
      const body = cuerpoError(e);
      if (body.error === 'puesto_con_perfiles') {
        setBloqueo(body.perfiles || []);
      } else {
        console.error('[incorp][puestos] desactivar', e);
        toast.error(body.message || 'No se pudo desactivar el puesto.');
      }
    } finally {
      setProcesando(false);
    }
  }

  const footer = bloqueo ? (
    <Button variant="outline" onClick={onClose}>Entendido</Button>
  ) : (
    <>
      <Button variant="outline" onClick={onClose} disabled={procesando}>Cancelar</Button>
      <Button variant="destructive" onClick={confirmar} disabled={procesando}>
        {procesando ? <Loader2 className="animate-spin" /> : <Ban />}Desactivar
      </Button>
    </>
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      tone="danger"
      title={bloqueo ? 'No se puede desactivar' : 'Desactivar puesto'}
      subtitle={puesto.nombre}
      footer={footer}
    >
      {bloqueo ? (
        <div className="space-y-3">
          <p className="text-sm text-v25-ink-700">
            Este puesto tiene {bloqueo.length} perfil(es) activo(s) que lo usan. Desactívalos primero.
          </p>
          <ul className="divide-y divide-v25-line rounded-v25-md border border-v25-line">
            {bloqueo.map((pf) => (
              <li key={pf.id} className="flex items-center gap-2 px-3 py-2 text-sm text-v25-ink-800">
                <UserCog className="h-4 w-4 shrink-0 text-v25-ink-400" />
                Perfil de {AMBITO_TP[pf.tipo_persona] || pf.tipo_persona}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-sm text-v25-ink-700">
          El puesto dejará de estar disponible para nuevos perfiles. Podrás reactivarlo después.
          ¿Seguro que quieres desactivar <strong>«{puesto.nombre}»</strong>?
        </p>
      )}
    </Modal>
  );
}
